import { FormEvent, useState } from 'react';
import { api } from '../lib/api';
import { FormField } from './FormField';
import { DependentMultiSelect } from './DependentMultiSelect';

interface CreateDiaryEntryFormProps {
  familyId: string;
  dependents: Array<{ id: string; name: string }>;
  onSuccess: () => void;
  onCancel: () => void;
}

export function CreateDiaryEntryForm({
  familyId,
  dependents,
  onSuccess,
  onCancel,
}: CreateDiaryEntryFormProps) {
  const [content, setContent] = useState('');
  const [selectedDepIds, setSelectedDepIds] = useState<string[]>(dependents.map((d) => d.id));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (!content.trim()) {
      setError('Please enter some content');
      return;
    }

    setLoading(true);

    try {
      await api.createDiaryEntry(familyId, selectedDepIds[0], content.trim(), selectedDepIds);
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Failed to create diary entry');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {error && (
        <div className="rounded-md bg-red-50 p-4 mb-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <DependentMultiSelect dependents={dependents} selectedIds={selectedDepIds} onChange={setSelectedDepIds} />

      <FormField label="Diary entry" id="diary-content" required>
        <textarea
          id="diary-content"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          className="input"
          rows={4}
          placeholder="Write about a visit, observation, or care note..."
          maxLength={2000}
          required
        />
        <p className="text-xs text-gray-500 mt-1">{content.length}/2000 characters</p>
      </FormField>

      <div className="flex gap-3 mt-8">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 btn btn-secondary"
          disabled={loading}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="flex-1 btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={loading}
        >
          {loading ? 'Saving...' : 'Add entry'}
        </button>
      </div>
    </form>
  );
}
